import { FileText, Plus } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Button } from '../../../ui/Button';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { NewOfferDialog } from './NewOfferDialog';
import { OfferRow } from './OfferRow';

/**
 * Oferty klienta (zakładka „Oferty” w profilu klienta; do użycia także w kompozycji): nagłówek „Oferty” z „Nowa
 * oferta” (moduł Klienci = Edycja), wiersze od najnowszej, ładowanie, pusty stan i błąd z ponowieniem.
 * Bez modułu Klienci nic nie pokazuje (oferty należą do modułu Klienci).
 */
export function OfferRows({
  clientId,
  limit = 50,
  showHeader = true,
  allowAdd = true,
}: {
  clientId: string;
  limit?: number;
  showHeader?: boolean;
  /** przycisk „Nowa oferta” (gdy osoba może zakładać oferty) */
  allowAdd?: boolean;
}) {
  const access = useAccess();
  const enabled = access.can('clients');
  const canAdd = allowAdd && access.can('clients', 'edit');
  const query = trpc.offers.list.useQuery({ clientId, filter: 'all', limit }, { enabled });
  const [dialogOpen, setDialogOpen] = useState(false);
  if (!enabled) return null;

  const addButton = canAdd && (
    <Button size="s" icon={Plus} onClick={() => setDialogOpen(true)}>
      Nowa oferta
    </Button>
  );

  let body: ReactNode;
  if (query.isPending) body = <ListSkeleton rows={2} />;
  else if (query.isError)
    body = (
      <ErrorState
        compact
        title="Nie udało się wczytać ofert"
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  else if (query.data.length === 0)
    body = (
      <EmptyState
        compact
        icon={FileText}
        title="Brak ofert"
        description="Oferta z zakresem, godzinami i warunkami. Wysyłka zawsze przez Akceptacje."
      />
    );
  else
    body = (
      <ul aria-label="Oferty klienta">
        {query.data.map((o) => (
          <li key={o.id}>
            <OfferRow offer={o} withClient={false} />
          </li>
        ))}
      </ul>
    );

  return (
    <Section aria-label="Oferty">
      {showHeader && <SectionHeader title="Oferty" tone="strong" action={addButton || undefined} />}
      {!showHeader && addButton && <div className="flex justify-end pb-2">{addButton}</div>}
      {body}
      {canAdd && <NewOfferDialog open={dialogOpen} onOpenChange={setDialogOpen} defaultClientId={clientId} />}
    </Section>
  );
}
